import {
    Autocomplete,
    Box,
    Checkbox,
    debounce,
    FormControl,
    FormHelperText,
    TextField,
    Tooltip,
    Typography,
    SxProps,
    Theme,
    IconButton,
} from '@mui/material'
import {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import TruncatedText from './TruncatedText'
import { AddRounded } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

export interface SelectItem {
    [key: string]: any
}

interface SearchInAPIAndMultiSelectInputProps {
    id: string
    label: string
    value: SelectItem[]
    placeholder?: string
    onSelect: (id: string, value: SelectItem[] | null) => void
    onSearch: (id: string, text: string) => void
    list: SelectItem[]
    required?: boolean
    disabled?: boolean
    isLegend?: boolean
    list_Height?: string
    hasAddNew?: boolean
    onClickAddNew?: () => void
    onClickRowIcon?: (item: SelectItem) => void
    errorMessage?: string
    onFetchNextPage?: () => void
    hasMore?: boolean
    isFetching?: boolean
    itemId?: string
    itemName?: string
    ui_width?: string
    hasItemsEdit?: boolean
    autoFocusOnMount?: boolean
    showSelectedCount?: boolean
    stopKeyboardPropagation?: boolean

    /** HEAD additions */
    showLabel?: boolean
    sx?: SxProps<Theme>
    useRawLabel?: boolean
    enableCustomHeight?: boolean
    inputHeight?: number | string

    /** develop additions */
    allowClear?: boolean
    getDisplayLabel?: (option: SelectItem) => string
}

const SearchInAPIAndMultiSelectInput = ({
    id,
    label,
    value = [],
    placeholder,
    onSelect,
    onSearch,
    list,
    required = false,
    disabled = false,
    isLegend = false,
    list_Height = '250px',
    hasAddNew = false,
    onClickAddNew = () => {},
    onClickRowIcon,
    errorMessage = '',
    onFetchNextPage,
    hasMore = false,
    isFetching = false,
    itemId = 'id',
    itemName = 'name',
    ui_width,
    hasItemsEdit,
    autoFocusOnMount,
    showSelectedCount = false,
    stopKeyboardPropagation = false,

    /** HEAD defaults */
    showLabel = true,
    useRawLabel = false,
    enableCustomHeight = false,
    inputHeight = 56,
    sx,

    /** develop defaults */
    allowClear = true,
    getDisplayLabel,
}: SearchInAPIAndMultiSelectInputProps): ReactElement => {
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const addNewOption = {
        [itemId]: 'ADD_NEW',
        [itemName]: '',
        hasItemEdit: false,
    }

    const fullList = useMemo(() => {
        const baseList = [...list]
        if (hasAddNew) baseList.unshift(addNewOption)
        return baseList
    }, [list, hasAddNew])

    useEffect(() => {
        if (autoFocusOnMount) inputRef.current?.focus()
    }, [autoFocusOnMount])

    const handleFocus = () => setIsFocused(true)

    const handleBlur = (e: any) => {
        if (e.target.value === '') debouncedSearch('')
        setIsFocused(false)
    }

    const debouncedSearch = useCallback(
        debounce((text: string) => onSearch(id, text), 300),
        [id],
    )

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (
            stopKeyboardPropagation &&
            [
                'ArrowDown',
                'ArrowUp',
                'ArrowRight',
                'ArrowLeft',
                'Enter',
            ].includes(e.key)
        ) {
            e.stopPropagation()
        }

        if (e.key === 'Backspace' && inputRef.current?.value === '') {
            e.stopPropagation()
            e.preventDefault()
        }
    }

    const handleInputChange = (
        _event: any,
        newInputValue: string,
        reason: string,
    ) => {
        if (reason === 'reset' || reason === 'blur') debouncedSearch('')
        else if (reason !== 'selectOption' && reason !== 'removeOption')
            debouncedSearch(newInputValue)
    }

    const handleChange = (_event: any, newValue: SelectItem[] | null) =>
        onSelect(id, newValue || [])

    const handleRowIcon = (e: any, item: any) => {
        e.stopPropagation()
        setIsOpen(false)
        setIsFocused(false)
        inputRef.current?.blur()
        onClickRowIcon?.(item)
    }

    const handleAddNewClick = () => {
        setIsOpen(false)
        setIsFocused(false)
        inputRef.current?.blur()
        onClickAddNew?.()
    }

    const getPlaceholderText = () => {
        if (value.length) return isFocused ? t('search') : ''
        if (useRawLabel) return label
        return placeholder || t('selectLabel', { label })
    }

    const labelCenterY =
        typeof inputHeight === 'number' && inputHeight < 40 ? '-7px' : '0px'

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl
                fullWidth
                disabled={disabled}
                size={isLegend ? undefined : 'small'}
            >
                {!isLegend && showLabel && (
                    <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
                        {label}
                        {required && '*'}
                    </Typography>
                )}

                <Autocomplete
                    id={`${id}-select`}
                    options={fullList}
                    value={value}
                    multiple
                    openOnFocus
                    open={isOpen}
                    onOpen={() => setIsOpen(true)}
                    onClose={() => setIsOpen(false)}
                    disableCloseOnSelect
                    getOptionLabel={(option) =>
                        option?.[itemId] === 'ADD_NEW'
                            ? t('addNew')
                            : getDisplayLabel
                              ? getDisplayLabel(option)
                              : option[itemName] || ''
                    }
                    onInputChange={handleInputChange}
                    onChange={handleChange}
                    isOptionEqualToValue={(opt, val) =>
                        opt[itemId] === val?.[itemId]
                    }
                    /** BOTH branches merged */
                    filterOptions={(options) => options}
                    disableClearable={!allowClear}
                    disabled={disabled}
                    loading={isFetching}
                    loadingText={t('loading')}
                    noOptionsText={isFetching ? t('loading') : t('noOptions')}
                    ListboxProps={{
                        style: {
                            maxHeight: list_Height,
                            ...(enableCustomHeight && { padding: 0 }),
                        },
                        onScroll: (event) => {
                            const listboxNode = event.currentTarget
                            const bottom =
                                listboxNode.scrollTop +
                                    listboxNode.clientHeight >=
                                listboxNode.scrollHeight - 10
                            if (bottom && hasMore && !isFetching)
                                onFetchNextPage?.()
                        },
                    }}
                    slotProps={{
                        clearIndicator: {
                            id: `${id}-clear-button`,
                            sx: { padding: 0 },
                        },
                        popupIndicator: { id: `${id}-dropdown-button` },
                        paper: {
                            sx: {
                                ...(ui_width ? { width: ui_width } : {}),
                            },
                        },
                    }}
                    renderOption={(props, option, { selected }) => {
                        const { key: _ignoredKey, ...restProps } = props
                        if (option?.[itemId] === 'ADD_NEW') {
                            return (
                                <Box
                                    component="li"
                                    id={id + 'add_new'}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%',
                                        cursor: 'pointer',
                                        padding: '2px 10px 5px 15px',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleAddNewClick()
                                    }}
                                >
                                    <AddRounded color="primary" />
                                    <Typography
                                        fontWeight="600"
                                        fontSize="14px"
                                        sx={{ ml: 1 }}
                                    >
                                        {t('addNew')}
                                    </Typography>
                                </Box>
                            )
                        }
                        return (
                            <Box
                                component="li"
                                key={option[itemId]}
                                {...restProps}
                                sx={{
                                    fontSize: '14px',
                                    ...(enableCustomHeight
                                        ? {
                                              minHeight: inputHeight,
                                              display: 'flex',
                                              alignItems: 'center',
                                              padding: '0 15px 0 10px',
                                              gap: '8px',
                                          }
                                        : {
                                              padding: '7px 15px 7px 10px',
                                          }),
                                }}
                            >
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    width={
                                        onClickRowIcon
                                            ? 'calc(100% - 20px)'
                                            : '100%'
                                    }
                                >
                                    <Checkbox
                                        size="small"
                                        checked={selected}
                                        sx={
                                            enableCustomHeight
                                                ? {
                                                      padding: 0,
                                                      marginRight: '6px',
                                                  }
                                                : { padding: '0px 5px 0 0' }
                                        }
                                    />
                                    <TruncatedText
                                        text={
                                            getDisplayLabel
                                                ? getDisplayLabel(option)
                                                : option?.[itemName]
                                        }
                                    />
                                </Box>
                                {((option.hasItemEdit &&
                                    option?.[itemId] !== '') ||
                                    hasItemsEdit) && (
                                    <IconButton
                                        id={`${id}-${option?.[itemName]}-edit`}
                                        onClick={(e) =>
                                            handleRowIcon(e, option)
                                        }
                                        aria-controls="simple-menu"
                                        aria-haspopup="true"
                                        key="edit"
                                        disabled={false}
                                        sx={{
                                            padding: '0px',
                                            marginLeft: 'auto',
                                            display: 'none',
                                        }}
                                        className="row-icon"
                                    >
                                        <img
                                            src="/assets/images/icons/edit.svg"
                                            alt="Edit"
                                        />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    }}
                    renderInput={(params) => (
                        <Tooltip
                            title={value.map((s) => s[itemName]).join(', ')}
                            arrow
                        >
                            <TextField
                                {...params}
                                id={`${id}-input`}
                                inputRef={inputRef}
                                onFocus={handleFocus}
                                onBlur={handleBlur}
                                label={
                                    showSelectedCount
                                        ? ''
                                        : isLegend
                                          ? `${useRawLabel ? label : t('selectLabel', { label })}${required ? ' *' : ''}`
                                          : ''
                                }
                                size={isLegend ? undefined : 'small'}
                                placeholder={getPlaceholderText()}
                                error={!!errorMessage}
                                onKeyDown={handleKeyDown}
                                InputLabelProps={{
                                    style: showSelectedCount
                                        ? { paddingRight: '48px' }
                                        : { paddingTop: '0px' },
                                }}
                                sx={{
                                    '& input::placeholder': {
                                        color: showSelectedCount
                                            ? 'black'
                                            : undefined,
                                        opacity: showSelectedCount ? 1 : 0.2,
                                        font: '-apple-system,BlinkMacSystemFont,"Segoe UI","Helvetica Neue",Helvetica,Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
                                    },
                                }}
                            />
                        </Tooltip>
                    )}
                    renderTags={(value) => {
                        if (!isFocused && useRawLabel) {
                            return (
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        lineHeight: 1,
                                        color: 'text.primary',
                                        maxWidth: '100%',
                                    }}
                                >
                                    {label}
                                </Typography>
                            )
                        }
                        if (showSelectedCount) {
                            return !isFocused && value.length > 0 ? (
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        lineHeight: 1,
                                        color: 'text.primary',
                                        maxWidth: '100%',
                                    }}
                                >
                                    {value.length} selected
                                </Typography>
                            ) : null
                        }
                        return (
                            <Typography
                                sx={{
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    lineHeight: 1,
                                    color: 'text.primary',
                                    maxWidth: isFocused
                                        ? 'calc(100% - 60px)'
                                        : '100%',
                                    opacity: disabled ? '0.4' : '1',
                                }}
                            >
                                {value.map((v) => v[itemName]).join(', ')}
                            </Typography>
                        )
                    }}
                    sx={[
                        {
                            width: '100%',
                            '& .MuiOutlinedInput-root': {
                                flexWrap: 'nowrap !important',
                            },

                            ...(showSelectedCount && {
                                '& .MuiOutlinedInput-root': {
                                    paddingRight: '48px !important',
                                    flexWrap: 'nowrap !important',
                                },
                                '& .MuiAutocomplete-endAdornment': {
                                    right: '8px !important',
                                },
                            }),
                            ...(!enableCustomHeight && {
                                '& > div > div': {
                                    paddingRight: showSelectedCount
                                        ? '48px !important'
                                        : '28px !important',
                                },
                            }),
                            ...(enableCustomHeight && {
                                '& .MuiOutlinedInput-root': {
                                    height: inputHeight,
                                    minHeight: inputHeight,
                                    padding: '0 35px 0 12px !important',
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'nowrap !important',
                                },
                                '& .MuiAutocomplete-input': {
                                    padding: '0 !important',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    minWidth: '50px !important',
                                },
                                '& .MuiInputLabel-root': {
                                    transform: `translate(14px, ${labelCenterY}) scale(1)`,
                                    '&.MuiInputLabel-shrink': {
                                        transform:
                                            'translate(14px, -9px) scale(0.75)',
                                    },
                                },
                                '& .MuiAutocomplete-endAdornment': {
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    right: '8px !important',
                                },
                                '& .MuiInputBase-sizeSmall': {
                                    paddingTop: '0 !important',
                                    paddingBottom: '0 !important',
                                },
                            }),

                            ...(!enableCustomHeight && {
                                '& > div > div': {
                                    paddingRight: '28px !important',
                                },
                            }),

                            '& .MuiAutocomplete-input': {
                                minWidth: isFocused
                                    ? '80px !important'
                                    : '0 !important',
                            },
                        },

                        ...(Array.isArray(sx) ? sx : [sx]),
                    ]}
                />

                {errorMessage && (
                    <FormHelperText sx={{ color: 'red' }}>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    )
}

export default SearchInAPIAndMultiSelectInput
